import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useContent } from "../content/ContentContext";
import { setPageMeta } from "../lib/seo";
import { SectionLabel, ButtonLink, Tag } from "../components/Ui";
import { Reveal, HeroLine, FadeIn } from "../components/Reveal";
import CaseCard from "../components/CaseCard";
import AboutSection from "../sections/AboutSection";
import ApproachSection from "../sections/ApproachSection";
import ToolsMarquee from "../components/ToolsMarquee";
import ServicesSection from "../sections/ServicesSection";
import PricingSection from "../sections/PricingSection";
import FaqSection from "../sections/FaqSection";

function Hero({ site }) {
  return (
    <section id="home" className="mx-auto max-w-7xl scroll-mt-24 px-4 pb-10 pt-4 sm:px-6 sm:pt-8 lg:px-8 lg:pb-14 lg:pt-8">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <h1 className="mt-6 font-serif text-4xl font-semibold leading-[1.12] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            {site.headline.map((line, i) => (
              <HeroLine key={line} delay={0.15 + i * 0.13}>
                {line.split(/(\*[^*]+\*)/).map((part, j) =>
                  part.length > 2 && part.startsWith("*") && part.endsWith("*") ? (
                    <em key={j} className="italic">{part.slice(1, -1)}</em>
                  ) : (
                    <span key={j}>{part}</span>
                  )
                )}
              </HeroLine>
            ))}
          </h1>
          <FadeIn delay={0.6}>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-ink">{site.sub}</p>
            <p className="mt-3 text-sm font-medium text-plum" data-testid="hero-location">{site.location}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <ButtonLink to="/#contact" testid="hero-start-project-button">Start a project</ButtonLink>
              <ButtonLink to="/case-studies" variant="secondary" testid="hero-explore-work-button">
                Explore my work
              </ButtonLink>
            </div>
          </FadeIn>
        </div>
        <div className="lg:col-span-5">
          <FadeIn delay={0.4}>
            <figure
              data-testid="hero-dashboard-image"
              className="overflow-hidden rounded-2xl border border-line bg-white shadow-[0_1px_0_#DED5EC]"
            >
              <img
                src="/images/dashboard-screens.jpg"
                alt="Dashboards showing performance reporting across multiple screens"
                className="h-auto w-full object-cover"
              />
            </figure>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

function WorkPreview({ caseStudies }) {
  const visible = caseStudies.filter((c) => !c.hidden).slice(0, 3);
  return (
    <section className="border-y border-line bg-lavender/40">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <SectionLabel>Selected work</SectionLabel>
              <h2 className="mt-4 max-w-2xl font-serif text-2xl font-medium tracking-tight text-ink sm:text-3xl lg:text-4xl">
                Real professional experience.
              </h2>
            </div>
            <Link to="/case-studies" data-testid="home-all-work-link" className="text-sm font-semibold text-plum hover:text-accent">
              Browse interactive case studies →
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
          {visible.map((cs, i) => (
            <Reveal key={cs.id} delay={i * 0.08}><CaseCard cs={cs} /></Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { content } = useContent();
  const { site, caseStudies } = content;
  useEffect(() => {
    setPageMeta(
      "Savvy Stephanie Analytics & Consulting — BI & Business Analysis",
      "Freelance BI and Business Analyst based in France. I turn messy data, manual reporting and unclear processes into clear dashboards and practical ways of working.",
      { path: "/", share: site.share }
    );
  }, [site.share]);
  return (
    <main>
      <Hero site={site} />
      <AboutSection />
      <ToolsMarquee />
      <ApproachSection />
      <ServicesSection />
      <PricingSection />
      <WorkPreview caseStudies={caseStudies} />
      <FaqSection />
    </main>
  );
}
